/**
 * Responsibility: Implement game store mutations behavior for the ui/stores surface.
 * Scope: Owns ui/stores concerns only; cross-surface orchestration is excluded.
 */
import type { GameState, AttributeKey, LogicUpdate, LogicForgeMeta, LivingEntityStats, NpcLogicUpdate, NPC } from "@anyquest/shared-core"
import { ActionCategory, QUEST_VALID_TRANSITIONS } from "@anyquest/shared-core"
import { formatClock, formatDay, totalMinFromClock } from '../../engine/temporal'
import { applyVitalDelta } from '../../engine/vitals'
import { playEffect } from '../../engine/audio'
import { applyArtifactReplacements, applyProgressClockUpdates, enqueueScheduledEffectJobs, formatProgressClock, normalizeSex } from '../../engine/state'
import { isMetaSystemNpcName } from '../../engine/npc-guard'
import { parseGameEventState } from '../../engine/entities/game-event-artifact'
import { TOPICS } from "@anyquest/shared-core"
import { asAttributeKey } from './game-store-helpers'

type MutationActor =
  | { kind: 'player' }
  | { kind: 'companion'; name?: string }

interface MutationDeps {
  emit: (topic: string, data?: unknown) => void
  actor?: MutationActor
}

function resolveMutationTarget(
  gs: GameState,
  actor?: MutationActor,
): GameState['player'] | GameState['companions'][number] {
  if (actor?.kind === 'companion') {
    const targetName = String(actor.name ?? '').trim().toLowerCase()
    if (targetName) {
      const companion = (gs.companions ?? []).find(c => c.name.toLowerCase() === targetName)
      if (companion) return companion
    }
  }
  return gs.player
}

function pruneEntityModifiers(entity: LivingEntityStats, totalMin: number): void {
  if (!entity.dormantEffects) entity.dormantEffects = []
  for (const rawKey of Object.keys(entity.modifiers)) {
    const key = asAttributeKey(rawKey)
    if (!key) continue
    const mods = entity.modifiers[key]
    if (!mods) continue
    const active: typeof mods = []
    for (const m of mods) {
      if (m.expiresAt == null || m.expiresAt > totalMin) {
        active.push(m)
        continue
      }
      entity.dormantEffects.push({
        label: m.label,
        source: m.source,
        expiredAt: m.expiresAt,
        attr: key,
      })
    }
    entity.modifiers[key] = active
    if (!entity.modifiers[key]?.length) delete entity.modifiers[key]
  }
  if (entity.dormantEffects.length > 60) {
    entity.dormantEffects = entity.dormantEffects
      .sort((a, b) => b.expiredAt - a.expiredAt)
      .slice(0, 60)
  }
}

function pruneExpiredModifiers(gs: GameState): void {
  const totalMin = totalMinFromClock(gs.world.clock)
  pruneEntityModifiers(gs.player, totalMin)
  for (const c of gs.companions ?? []) pruneEntityModifiers(c, totalMin)
}

function prettifyRoomFlag(key: string, value: string | boolean | number | null | undefined): string {
  const base = key.replace(/_/g, ' ').toUpperCase()
  if (value === true) return base
  if (value === false || value == null) return base
  return `${base}: ${value}`
}

function archiveDormantRoomFlag(gs: GameState, key: string, previousValue: string | boolean | number | null | undefined): void {
  if (previousValue === undefined) return
  const totalMin = totalMinFromClock(gs.world.clock)
  const room = gs.currentRoom
  room.dormantFlags = room.dormantFlags ?? []
  room.dormantFlags.push({
    key,
    label: prettifyRoomFlag(key, previousValue),
    clearedAt: totalMin,
  })
  if (room.dormantFlags.length > 60) {
    room.dormantFlags = room.dormantFlags
      .sort((a, b) => b.clearedAt - a.clearedAt)
      .slice(0, 60)
  }
}

function appendClockEvents(gs: GameState, updates: Record<string, number> | null | undefined): void {
  const applied = applyProgressClockUpdates(gs, updates)
  if (!applied.advanced.length) return
  const time = `${formatDay(gs.world.clock)}, ${formatClock(gs.world.clock)}`
  gs.worldLog = gs.worldLog ?? []
  for (const hit of applied.advanced) {
    const status = formatProgressClock(hit.clock)
    const text = hit.after >= hit.clock.segments && hit.before < hit.clock.segments
      ? `Clock filled (${hit.scope}): ${status}${hit.clock.trigger ? ` — ${hit.clock.trigger}` : ''}`
      : `Clock advanced (${hit.scope}): ${status}`
    gs.worldLog.push({ time, text, type: 'world' })
  }
  if (gs.worldLog.length > 30) gs.worldLog = gs.worldLog.slice(-30)
}

export function applyLogicUpdates(
  gs: GameState,
  logicUpdate: LogicUpdate,
  meta: LogicForgeMeta,
  input: string,
  deps: MutationDeps,
): void {
  const { emit } = deps
  const target = resolveMutationTarget(gs, deps.actor)
  const { updates } = logicUpdate
  const totalMin = totalMinFromClock(gs.world.clock)

  if (meta.category === ActionCategory.ability) {
    const abilityName = input.replace(/^use ability:\s*/i, '').split('|')[0]?.trim()
    if (abilityName) {
      const ability = target.abilities.find(a => a.name.toLowerCase() === abilityName.toLowerCase())
      if (ability) ability.lastUsed = totalMin
    }
  }

  if (typeof updates.vitality === 'number') {
    const clampedDelta = updates.vitality < 0 ? Math.max(-40, updates.vitality) : updates.vitality
    applyVitalDelta(target.vitals, 'vitality', clampedDelta)
  }
  if (typeof updates.spirit === 'number') {
    applyVitalDelta(target.vitals, 'spirit', updates.spirit)
  }
  if (typeof updates.essence === 'number') {
    applyVitalDelta(target.vitals, 'essence', updates.essence)
  }

  if (updates.mods) {
    for (const [k, mod] of Object.entries(updates.mods)) {
      const key = asAttributeKey(k)
      if (!key) continue
      if (!target.attributes[key]) continue
      if (!target.modifiers[key]) target.modifiers[key] = []
      target.modifiers[key]!.push({ ...mod, expiresAt: mod.expiresAt ?? totalMin + 120 })
    }
  }

  const currentLocKey = `${gs.player.location.x}_${gs.player.location.y}`
  const currentNode = gs.atlas.nodes[currentLocKey]

  if (updates.inventoryAdd?.length) {
    const existingNames = new Set(target.inventory.map(i => i.name.toLowerCase()))
    const safeItems = updates.inventoryAdd
      .map(i => {
        if (typeof i === 'string') {
          const name = i.trim()
          return { name, desc: name, icon: '📦' }
        }
        if (typeof i === 'object' && i !== null) {
          const name = (i as any).name?.trim() || (i as any).item?.trim() || ''
          return {
            name,
            desc: (i as any).desc?.trim() || name,
            icon: (i as any).icon || '📦',
            worth: typeof (i as any).worth === 'number' ? (i as any).worth : undefined,
            actions: Array.isArray((i as any).actions) ? (i as any).actions : undefined,
          }
        }
        return { name: '', desc: '', icon: '📦', worth: undefined, actions: undefined }
      })
      .filter(i => i.name.length >= 2 && i.name.length <= 80 && !existingNames.has(i.name.toLowerCase()))
    target.inventory.push(...safeItems.map(item => ({ 
      name: item.name, 
      desc: item.desc, 
      icon: item.icon, 
      worth: item.worth,
      ...(Array.isArray(item.actions) ? { actions: item.actions } : {}),
      equipped: false 
    } as any)))
    // Clean up room items if the player picked them up
    for (const item of safeItems) {
      const roomIdx = gs.currentRoom.items.findIndex(i => typeof i === 'string' ? i.toLowerCase() === item.name.toLowerCase() : (i as any).name?.toLowerCase() === item.name.toLowerCase())
      if (roomIdx >= 0) gs.currentRoom.items.splice(roomIdx, 1)
    }
  }
  if (updates.inventoryRemove?.length) {
    target.inventory = target.inventory.filter(i => !updates.inventoryRemove!.includes(i.name))
  }
  if (updates.inventoryEquip?.length) {
    target.inventory.forEach(i => {
      if (updates.inventoryEquip!.includes(i.name)) i.equipped = true
    })
  }
  if (updates.inventoryUnequip?.length) {
    target.inventory.forEach(i => {
      if (updates.inventoryUnequip!.includes(i.name)) i.equipped = false
    })
  }

  if (updates.roomItemsAdd?.length) {
    const safeRoomItems = updates.roomItemsAdd.map(i => {
      if (typeof i === 'string') return i
      if (typeof i === 'object' && i !== null) {
        return {
          name: (i as any).name || (i as any).item || 'unknown',
          desc: typeof (i as any).desc === 'string' ? (i as any).desc.trim() : undefined,
          icon: typeof (i as any).icon === 'string' ? (i as any).icon : undefined,
          worth: typeof (i as any).worth === 'number' ? (i as any).worth : undefined,
          actions: Array.isArray((i as any).actions) ? (i as any).actions : undefined,
        }
      }
      return 'unknown'
    })
    gs.currentRoom.items.push(...(safeRoomItems as any))
    if (currentNode) currentNode.items = [...gs.currentRoom.items]
  }
  if (updates.roomNpcsAdd?.length) {
    for (const raw of updates.roomNpcsAdd) {
      if (!raw?.name || !raw?.identity) continue
      if (isMetaSystemNpcName(raw.name)) continue
      const npc: NPC = {
        name: raw.name,
        identity: raw.identity,
        lore: raw.lore ?? `${raw.name} — ${raw.identity}`,
        sex: normalizeSex(raw.sex),
        relationship: 0,
        intentions: [],
        memory: [],
        inventory: [],
      }
      // Avoid duplicate names in the same room
      if (!gs.currentRoom.npcs.some(n => n.name.toLowerCase() === npc.name.toLowerCase())) {
        gs.currentRoom.npcs.push(npc)
      }
    }
  }
  if (updates.roomItemsRemove?.length) {
    gs.currentRoom.items = gs.currentRoom.items.filter(i => !updates.roomItemsRemove!.includes(i))
    if (currentNode) currentNode.items = [...gs.currentRoom.items]
  }

  const itemPickedUp = !!updates.inventoryAdd?.length || !!updates.roomItemsRemove?.length
  const itemDropped = !!updates.inventoryRemove?.length || !!updates.roomItemsAdd?.length
  if (itemPickedUp) playEffect('item_pickup')
  else if (itemDropped) playEffect('item_drop')

  if (updates.roomFlags) {
    if (!gs.currentRoom.flags) gs.currentRoom.flags = {}
    gs.currentRoom.dormantFlags = gs.currentRoom.dormantFlags ?? []
    for (const [key, val] of Object.entries(updates.roomFlags)) {
      if (val === null) {
        archiveDormantRoomFlag(gs, key, gs.currentRoom.flags[key])
        delete gs.currentRoom.flags[key]
      } else {
        gs.currentRoom.flags[key] = val
      }
    }
    if (currentNode) {
      currentNode.flags = { ...gs.currentRoom.flags }
      currentNode.dormantFlags = [...(gs.currentRoom.dormantFlags ?? [])]
    }
  }

  if (updates.clockUpdates) appendClockEvents(gs, updates.clockUpdates)

  if (updates.npcRelationshipUpdates) {
    for (const [name, rel] of Object.entries(updates.npcRelationshipUpdates)) {
      const wNpcs = gs.world.npcs.filter(n => n.name.toLowerCase() === name.toLowerCase())
      wNpcs.forEach(n => {
        // Clamp relationship deltas to ±20 per action to prevent jarring 0→100 jumps
        const current = n.relationship ?? 0
        const delta = Math.max(-20, Math.min(20, (rel ?? 0) - current))
        n.relationship = Math.max(-100, Math.min(100, current + delta))
      })
      const rNpcs = gs.currentRoom.npcs.filter(n => n.name.toLowerCase() === name.toLowerCase())
      rNpcs.forEach(n => {
        const current = n.relationship ?? 0
        const delta = Math.max(-20, Math.min(20, (rel ?? 0) - current))
        n.relationship = Math.max(-100, Math.min(100, current + delta))
      })
    }
  }
  if (updates.npcMemoryAdd) {
    for (const [name, mem] of Object.entries(updates.npcMemoryAdd)) {
      const wNpcs = gs.world.npcs.filter(n => n.name.toLowerCase() === name.toLowerCase())
      wNpcs.forEach(n => {
        n.memory = n.memory ?? []
        n.memory.push(mem)
        if (n.memory.length > 10) n.memory.shift()
      })
      const rNpcs = gs.currentRoom.npcs.filter(n => n.name.toLowerCase() === name.toLowerCase())
      rNpcs.forEach(n => {
        n.memory = n.memory ?? []
        if (!n.memory.includes(mem)) n.memory.push(mem)
        if (n.memory.length > 10) n.memory.shift()
      })
    }
  }

  if (updates.questUpdates?.length) {
    let completedNow = 0
    for (const qu of updates.questUpdates) {
      const quest = gs.journal.find(q => q.id === qu.id)
      if (!quest) continue
      const allowed = QUEST_VALID_TRANSITIONS[quest.status]
      if (!allowed?.includes(qu.newStatus)) continue

      // Guard: block completion if prerequisite quests aren't completed
      if (qu.newStatus === 'completed' && Array.isArray(quest.dependsOn) && quest.dependsOn.length) {
        const allDepsMet = quest.dependsOn.every(depId => {
          const dep = gs.journal.find(q => q.id === depId)
          return dep?.status === 'completed'
        })
        if (!allDepsMet) continue
      }

      const wasStatus = quest.status
      quest.status = qu.newStatus
      if (qu.newStatus === 'completed' || qu.newStatus === 'failed') {
        quest.completed = totalMin
      }
      if (wasStatus !== 'completed' && qu.newStatus === 'completed') completedNow += 1
    }
    if (completedNow > 0) playEffect('level_up')
  }

  applyArtifactReplacements(gs, updates.artifactReplacements)
  if (updates.scheduleJobs?.length) {
    enqueueScheduledEffectJobs(gs, updates.scheduleJobs)
  }
  if (!updates.artifactReplacements && updates.clearEvent) {
    gs.activeEvent = null
  }
  if (!updates.artifactReplacements && updates.triggerEvent) {
    const nextEvent = parseGameEventState(updates.triggerEvent)
    if (nextEvent) {
      gs.activeEvent = nextEvent
    }
  }

  if (updates.inventoryAdd?.length || updates.inventoryRemove?.length || updates.inventoryEquip?.length || updates.inventoryUnequip?.length) {
    emit(TOPICS.game.inventoryChanged)
  }

  pruneExpiredModifiers(gs)
  emit(TOPICS.game.vitalsChanged, gs.player.vitals)
}

export function applyNpcResult(gs: GameState, npcResult: NpcLogicUpdate, deps: MutationDeps): void {
  const { emit } = deps
  const totalMin = totalMinFromClock(gs.world.clock)
  if (npcResult.vitalityDelta) {
    const clampedNpcDelta = npcResult.vitalityDelta < 0 ? Math.max(-40, npcResult.vitalityDelta) : npcResult.vitalityDelta
    applyVitalDelta(gs.player.vitals, 'vitality', clampedNpcDelta)
  }
  if (npcResult.spiritDelta) {
    applyVitalDelta(gs.player.vitals, 'spirit', npcResult.spiritDelta)
  }
  if (npcResult.essenceDelta) {
    applyVitalDelta(gs.player.vitals, 'essence', npcResult.essenceDelta)
  }
  if ((npcResult.vitalityDelta ?? 0) < 0) {
    playEffect('danger')
    emit(TOPICS.ui.playerDamaged, { delta: npcResult.vitalityDelta })
  } else if ((npcResult.spiritDelta ?? 0) < 0) {
    playEffect('danger')
  }
  if (npcResult.mods) {
    for (const [k, mod] of Object.entries(npcResult.mods)) {
      const key = asAttributeKey(k)
      if (!key || !mod) continue
      if (!gs.player.modifiers[key]) gs.player.modifiers[key] = []
      gs.player.modifiers[key]!.push({
        val: mod.val,
        label: mod.label,
        source: mod.source,
        expiresAt: totalMin + (mod.duration ?? 120),
      })
    }
  }
  if (npcResult.inventorySteal) {
    const stealName = npcResult.inventorySteal
    const idx = gs.player.inventory.findIndex(i => i.name === stealName)
    if (idx >= 0) {
      gs.player.inventory.splice(idx, 1)
      gs.currentRoom.items.push(stealName)
      emit(TOPICS.game.inventoryChanged)
    }
  }
  emit(TOPICS.game.vitalsChanged, gs.player.vitals)
}
